/**
 * Upcoming Recurring Transactions Card
 *
 * Shows active recurring transactions due in the next 30 days,
 * drawn from the full recurring transactions dataset.
 * In live mode, fetches from the backend API; in demo mode, uses static demo data.
 */

import React, { useState, useEffect } from 'react';
import { Calendar, ArrowRight, Loader2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { InfoTooltip } from '@/components/tours/InfoTooltip';
import { usePermissions } from '@/hooks/usePermissions';
import { useFormatting } from '@/hooks/useFormatting';
import { useApp } from '@/contexts/AppContext';
import { getActiveRecurringTransactions } from '@/data/recurring-transactions-data';
import { getAllRecurringTransactions } from '@/api/services/recurringTransactionService';
import type { RecurringTransactionApi } from '@/api/models/recurringTransaction';
import { format } from 'date-fns';

interface UpcomingItem {
  id: string | number;
  name: string;
  amount: number | null;
  isVariable: boolean;
  frequency: string;
  nextDueDate: Date;
}

interface UpcomingRecurringTransactionsCardProps {
  onNavigate?: (page: string) => void;
}

export default function UpcomingRecurringTransactionsCard({ onNavigate }: UpcomingRecurringTransactionsCardProps) {
  const { hasPermission } = usePermissions();
  const { formatCurrency } = useFormatting();
  const { mode } = useApp();

  const [liveTransactions, setLiveTransactions] = useState<UpcomingItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (mode === 'live') {
      setIsLoading(true);
      setError(null);
      getAllRecurringTransactions()
        .then((response) => {
          const now = new Date();
          now.setHours(0, 0, 0, 0);
          const future = new Date(now);
          future.setDate(future.getDate() + 30);

          const mapped: UpcomingItem[] = response.items
            .filter((t: RecurringTransactionApi) => {
              if (t.status !== 'Active') return false;
              const dueDate = new Date(t.nextDueDate);
              return dueDate >= now && dueDate <= future;
            })
            .map((t: RecurringTransactionApi) => ({
              id: t.id,
              name: t.name,
              amount: t.amount,
              isVariable: t.isVariable,
              frequency: t.frequency,
              nextDueDate: new Date(t.nextDueDate),
            }))
            .sort((a: UpcomingItem, b: UpcomingItem) => a.nextDueDate.getTime() - b.nextDueDate.getTime());

          setLiveTransactions(mapped);
        })
        .catch((err) => {
          console.error('Failed to fetch recurring transactions:', err);
          setError('Failed to load recurring transactions');
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [mode]);

  if (!hasPermission('view_upcoming_recurring_transactions_card')) {
    return null;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const in30Days = new Date(today);
  in30Days.setDate(in30Days.getDate() + 30);

  const upcoming: UpcomingItem[] = mode === 'live'
    ? liveTransactions
    : getActiveRecurringTransactions()
        .filter(rt => rt.nextDueDate >= today && rt.nextDueDate <= in30Days)
        .sort((a, b) => a.nextDueDate.getTime() - b.nextDueDate.getTime())
        .map(rt => ({
          id: rt.id,
          name: rt.name,
          amount: rt.amount,
          isVariable: rt.isVariable,
          frequency: rt.frequency,
          nextDueDate: rt.nextDueDate,
        }));

  const formatAmount = (amount: number | null, isVariable: boolean) => {
    if (isVariable) return 'Variable';
    return formatCurrency(amount ?? 0, 'full');
  };

  if (isLoading && mode === 'live') {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Upcoming Recurring Transactions
          </CardTitle>
          <CardDescription>Next 30 days</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error && mode === 'live') {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Upcoming Recurring Transactions
          </CardTitle>
          <CardDescription>Next 30 days</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8 text-destructive">
            {error}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (upcoming.length === 0) {
    return (
      <Card>
        <CardHeader className="border-b">
          <span className="text-lg font-semibold text-neutral-900">Upcoming Recurring Transactions</span>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground text-center py-8">
            No recurring transactions due in the next 30 days
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="border-b">
        <div className="flex items-center gap-1.5">
          <CardTitle className="text-lg font-semibold text-neutral-900">Upcoming Recurring Transactions</CardTitle>
          <InfoTooltip
            title="Upcoming Recurring Transactions"
            content="Manually scheduled payments and receipts due in the next 30 days — rent, payroll, VAT, insurance and similar fixed obligations. These are separate from supplier bills and are automatically included in your cash forecast."
          />
        </div>
        <CardDescription>
          {upcoming.length} transaction{upcoming.length !== 1 ? 's' : ''} due in the next 30 days
        </CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-y-auto max-h-[320px] px-6 py-4 space-y-3">
          {upcoming.map((rt) => {
            const daysUntil = Math.ceil(
              (rt.nextDueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 60 * 24)
            );
            const isUrgent = daysUntil <= 7;
            const isDueToday = daysUntil === 0;

            const iconBg = isDueToday
              ? 'var(--mc-error-bg)'
              : isUrgent
              ? 'var(--mc-warning-bg)'
              : 'var(--mc-success-bg)';

            const iconColor = isDueToday
              ? 'var(--mc-error-icon)'
              : isUrgent
              ? 'var(--mc-warning-icon)'
              : 'var(--mc-success-icon)';

            return (
              <div
                key={rt.id}
                className="flex items-center justify-between p-3 border rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <span
                    className="text-xs font-semibold px-2 py-1 rounded-md flex-shrink-0 min-w-[44px] text-center"
                    style={{ backgroundColor: iconBg, color: iconColor }}
                  >
                    {isDueToday ? 'Today' : `${daysUntil}d`}
                  </span>
                  <div>
                    <p className="font-medium text-sm">{rt.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {format(rt.nextDueDate, 'd MMM yyyy')} · {rt.frequency}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium tabular-nums text-right">
                    {formatAmount(rt.amount, rt.isVariable)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
        <div className="px-6 py-3 border-t flex justify-end">
          <Button
            variant="ghost"
            size="sm"
            className="text-neutral-900 hover:bg-teal-50 hover:text-teal-700"
            onClick={() => onNavigate?.('recurring-transactions')}
          >
            View all recurring transactions
            <ArrowRight className="h-3 w-3 ml-1" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
